/**
 * Comment le catalogue nomme la difficulté d'un morceau.
 *
 * # Deux axes, jamais fondus
 *
 * Le catalogue expose un `difficulty` de 1 à 5 et un `noteCount`. Ils ne
 * répondent pas à la même question, et les mélanger produit exactement les
 * faux positifs qu'on observe aujourd'hui :
 *
 *  - **le geste** — ce que la main doit savoir faire. C'est `difficulty`, qui
 *    est la moyenne de la *pire fenêtre de quatre mesures* : une mesure
 *    d'arpèges rapides suffit à faire monter le niveau d'une pièce par
 *    ailleurs paisible ;
 *  - **le chantier** — combien de temps ça va prendre. C'est `noteCount` : un
 *    morceau de 250 notes et un morceau de 2000 notes ne se travaillent pas
 *    dans la même unité de temps, même à geste identique.
 *
 * `difficulty` étant calculé sur une fenêtre, il est **aveugle à la longueur** :
 * le Nocturne op. posth. de Chopin en ressort au niveau 1 alors qu'il dure
 * 5 min 57. D'où le garde-fou de durée ci-dessous, et d'où le fait que le
 * chantier soit affiché *à côté* du geste et jamais dedans.
 *
 * # Pourquoi on ne dit plus « Intermédiaire »
 *
 * Les anciens libellés (`Débutant / Facile / Intermédiaire / Avancé / Expert`)
 * nommaient **l'élève**, pas la pièce. Dire à un adulte que le morceau qu'il
 * veut jouer est « Intermédiaire », c'est lui dire qu'il ne l'est pas. Les
 * libellés ci-dessous nomment **le geste** : ils décrivent la pièce, sont
 * vérifiables en la regardant, et ne classent personne.
 *
 * # Le chiffre ne s'affiche plus
 *
 * `difficulty` reste une donnée de tri (`sort=playability`) mais ne paraît
 * nulle part à l'écran, pas même dans un `aria-label` : un lecteur d'écran qui
 * annonce « difficulté 4 sur 5 » réintroduit le classement que les libellés
 * viennent d'enlever.
 *
 * Module pur : ni React, ni DOM. Testé dans `__tests__/difficulty-tiers.test.ts`.
 */

/** Palier de geste, 1..5. Miroir des valeurs de `CatalogPiece.difficulty`. */
export type GestureTier = 1 | 2 | 3 | 4 | 5;

export type GestureTierLabel = {
  /** Sur la fiche, où la place ne manque pas. */
  readonly long: string;
  /** Sur une carte, où la ligne partage sa largeur avec le chantier. */
  readonly short: string;
};

export const GESTURE_TIERS: Record<GestureTier, GestureTierLabel> = {
  1: { long: "Une main pose, l'autre suit", short: "Une main pose" },
  2: { long: "Les deux mains travaillent", short: "Deux mains" },
  3: { long: "La main se déplace", short: "La main se déplace" },
  4: { long: "Le doigt doit être rapide", short: "Doigt rapide" },
  5: { long: "Répertoire de concert", short: "Concert" },
};

/**
 * Au-delà de cette durée, le palier 1 est interdit.
 *
 * Trois minutes, parce que « une main pose, l'autre suit » promet un morceau
 * qu'on lit d'un bout à l'autre dans la soirée. Une pièce plus longue peut
 * n'avoir aucune mesure difficile et rester un projet : c'est le cas des
 * nocturnes lents, que la fenêtre de quatre mesures classe au niveau 1. Le
 * garde-fou ne corrige pas la mesure, il refuse seulement la promesse qu'elle
 * ferait tenir au produit.
 */
const TIER_ONE_MAX_DURATION_SEC = 180;

/**
 * Palier de geste affiché pour une pièce, `null` quand le catalogue n'a pas de
 * niveau (un import que personne n'a analysé).
 *
 * `durationSec` n'est pas décoratif : il arme le garde-fou. Passer `null`
 * revient à dire « durée inconnue », et le palier brut est alors rendu tel
 * quel — on ne remonte pas un morceau d'un cran sur une simple absence de
 * donnée.
 */
export function gestureTier(
  difficulty: number | null,
  durationSec: number | null,
): GestureTier | null {
  if (
    difficulty === null ||
    !Number.isInteger(difficulty) ||
    difficulty < 1 ||
    difficulty > 5
  ) {
    return null;
  }

  const tier = difficulty as GestureTier;

  if (
    tier === 1 &&
    durationSec !== null &&
    durationSec > TIER_ONE_MAX_DURATION_SEC
  ) {
    return 2;
  }

  return tier;
}

/**
 * Le chantier : l'unité de temps dans laquelle le morceau se travaille.
 *
 * Volontairement des durées vécues (« une soirée ») et non des chiffres
 * (« 250 notes ») : personne ne sait ce que pèsent 250 notes, tout le monde
 * sait ce que pèse une soirée.
 */
export type WorkSize = "evening" | "week" | "month" | "project";

export const WORK_SIZE_LABELS: Record<WorkSize, string> = {
  evening: "Une soirée",
  week: "Une semaine",
  month: "Un mois",
  project: "Un projet",
};

/** Bornes hautes, en nombre de notes. La dernière classe n'en a pas. */
const NOTE_COUNT_BOUNDS: readonly (readonly [number, WorkSize])[] = [
  [250, "evening"],
  [600, "week"],
  [1500, "month"],
];

/**
 * Repli sur la durée, quand le score n'a pas été compté.
 *
 * Les bornes sont celles en notes ramenées à ~3,3 notes par seconde, la
 * densité moyenne observée sur le catalogue. C'est plus grossier — deux
 * morceaux de même durée peuvent demander un travail très différent — donc ce
 * repli existe pour ne pas laisser une carte muette, pas pour remplacer le
 * compte de notes.
 */
const DURATION_BOUNDS: readonly (readonly [number, WorkSize])[] = [
  [75, "evening"],
  [180, "week"],
  [420, "month"],
];

/**
 * Chantier d'un morceau, `null` quand ni le compte de notes ni la durée ne
 * sont connus.
 *
 * `null` est un résultat, pas un échec : mieux vaut une carte sans chantier
 * qu'un chantier inventé. L'appelant n'affiche alors simplement rien.
 */
export function workSize(
  noteCount: number | null,
  durationSec: number | null,
): WorkSize | null {
  if (noteCount !== null && noteCount > 0) {
    return classify(noteCount, NOTE_COUNT_BOUNDS);
  }

  if (durationSec !== null && durationSec > 0) {
    return classify(durationSec, DURATION_BOUNDS);
  }

  return null;
}

function classify(
  value: number,
  bounds: readonly (readonly [number, WorkSize])[],
): WorkSize {
  for (const [max, size] of bounds) {
    if (value <= max) {
      return size;
    }
  }

  return "project";
}

/**
 * La ligne de description d'une carte : « Une main pose · Une soirée ».
 *
 * Renvoie `null` quand rien n'est connu, pour que l'appelant n'ait pas à
 * décider de l'affichage d'un séparateur orphelin.
 */
export function pieceTierLine(
  difficulty: number | null,
  noteCount: number | null,
  durationSec: number | null,
): string | null {
  const tier = gestureTier(difficulty, durationSec);
  const size = workSize(noteCount, durationSec);
  const parts: string[] = [];

  if (tier !== null) {
    parts.push(GESTURE_TIERS[tier].short);
  }

  if (size !== null) {
    parts.push(WORK_SIZE_LABELS[size]);
  }

  if (parts.length === 0) {
    return null;
  }

  return parts.join(" · ");
}
